import {
  Injectable,
  ComponentRef,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
} from '@angular/core';
import { Subject } from 'rxjs';
import { PdfViewerConfig, PdfViewerState } from '../shared/components/pdf-preview/pdf-viewer.types';
import { PdfPreview } from '../shared/components/pdf-preview/pdf-preview';
import { NavigationStart, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class PdfViewerService {
  private overlayRef?: ComponentRef<PdfPreview>;
  private stateSubject = new Subject<PdfViewerState>();
  private overlayContainer?: HTMLElement;

  private closeSubscription?: { unsubscribe(): void };

  state$ = this.stateSubject.asObservable();

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private router: Router,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.close();
      }
    });
  }

  /** Open PDF in global overlay modal */
  open(config: PdfViewerConfig): void {
    this.close(); // safe now — no async gap

    this.overlayContainer = document.createElement('div');
    this.overlayContainer.className = 'pdf-viewer-overlay';
    document.body.appendChild(this.overlayContainer);

    // No requestAnimationFrame needed — container is already in DOM
    this.createViewer(config);
  }

  private createViewer(config: PdfViewerConfig): void {
    if (!this.overlayContainer) return;

    this.overlayRef = createComponent(PdfPreview, {
      environmentInjector: this.injector,
      hostElement: this.overlayContainer,
    });

    this.overlayRef.instance.config = { ...this.getDefaultConfig(), ...config };
    this.overlayRef.instance.isOverlay = true;
    this.closeSubscription = this.overlayRef.instance.close.subscribe(() => this.close());

    this.appRef.attachView(this.overlayRef.hostView);
    this.overlayRef.changeDetectorRef.detectChanges();

    document.body.style.overflow = 'hidden';
  }

  close(): void {
    this.closeSubscription?.unsubscribe();
    this.closeSubscription = undefined;

    if (this.overlayRef) {
      this.appRef.detachView(this.overlayRef.hostView);
      this.overlayRef.destroy();
      this.overlayRef = undefined;
    }

    if (this.overlayContainer?.parentNode) {
      this.overlayContainer.parentNode.removeChild(this.overlayContainer);
      this.overlayContainer = undefined;
    }

    document.body.style.overflow = '';
  }

  private getDefaultConfig(): Omit<PdfViewerConfig, 'src'> {
    return {
      renderText: true,
      showPrint: true,
      showDownload: true,
      showZoom: true,
      showNavigation: true,
      initialZoom: 1,
      initialPage: 1,
      backdropBlur: true,
    };
  }
}
